import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'

export interface OverviewMonth {
  month: string
  revenue: number
  orders: number
  events: number
}
export interface OverviewProduct {
  name: string
  sku: string | null
  quantity: number
  orders: number
  image?: string | null
  /** Mã sản phẩm nguồn (itemsCode Evotech). */
  itemsCode?: string | null
  /** Link sản phẩm (Webapp / Mini App) dựng ở máy chủ. */
  link?: string | null
}
export interface RfmResult {
  recencyDays: number | null
  frequency: number
  monetary: number
  r: number
  f: number
  m: number
}
export interface ContactOverview {
  orderCount: number
  totalSpent: number
  avgOrder: number
  lastOrderAt: string | null
  spendChangePct: number | null
  trend: OverviewMonth[]
  topProducts: OverviewProduct[]
  rfm: RfmResult | null
}

/** Chi tiêu, xu hướng theo tháng, sản phẩm hay mua, RFM của một khách. */
export function useContactOverview(id: string | undefined, source?: string) {
  return useQuery<ContactOverview>({
    queryKey: ['contact', id, 'overview', source ?? 'all'],
    enabled: !!id,
    staleTime: 60_000,
    queryFn: async () => (await api.get(`/contacts/${id}/overview`, { params: source ? { source } : undefined })).data,
  })
}

export interface ContactSegmentRef {
  id: string
  name: string
  description: string | null
  enteredAt: string
}

export function useContactSegments(id: string | undefined) {
  return useQuery<{ segments: ContactSegmentRef[] }>({
    queryKey: ['contact', id, 'segments'],
    enabled: !!id,
    queryFn: async () => (await api.get(`/contacts/${id}/segments`)).data,
  })
}

export interface TimelineItem {
  type: 'message' | 'note' | 'appointment' | 'event' | 'lifecycle'
  timestamp: string
  data: any
}

export function useContactTimeline(id: string | undefined, limit = 50) {
  return useQuery<{ items: TimelineItem[]; total: number }>({
    queryKey: ['contact', id, 'timeline', limit],
    enabled: !!id,
    queryFn: async () => (await api.get(`/contacts/${id}/timeline`, { params: { limit } })).data,
  })
}

// ── Nguồn của từng trường hồ sơ + các nguồn liên kết ──────────────────
export type FieldKey = 'fullName' | 'phone' | 'email' | 'avatarUrl' | 'jobTitle'

export interface FieldSourceInfo {
  label: string
  current: string | null
  source: { key: string; label: string; at: string | null } | null
  /** Giá trị khác mà các nguồn khác báo về (chưa được xử lý). */
  others: { source: string; label: string; value: string; at: string }[]
}

export function useContactFieldSources(id: string | undefined) {
  return useQuery<{ fields: Record<FieldKey, FieldSourceInfo> }>({
    queryKey: ['contact', id, 'field-sources'],
    enabled: !!id,
    queryFn: async () => (await api.get(`/contacts/${id}/field-sources`)).data,
  })
}

export function useResolveFieldSource(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { field: FieldKey; source?: string; keep?: boolean }) =>
      (await api.post(`/contacts/${id}/field-sources/resolve`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact', id] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export interface LinkedSources {
  channels: { id: string; platform: string; name: string; conversations: number; lastAt: string | null }[]
  orders: { source: string; count: number; total: number; lastAt: string | null }[]
  events: { source: string; count: number; lastAt: string | null }[]
  /** ĐỘNG theo nền tảng tổ chức đã kết nối (hoặc khách đã có định danh) — chỉ nền tảng định danh được mới có mặt. */
  identities: { key: string; label: string; linked: boolean; hint?: string }[]
}

export function useLinkedSources(id: string | undefined) {
  return useQuery<LinkedSources>({
    queryKey: ['contact', id, 'linked-sources'],
    enabled: !!id,
    staleTime: 30_000,
    queryFn: async () => (await api.get(`/contacts/${id}/linked-sources`)).data,
  })
}

/** Dữ liệu hồ sơ ĐỘNG của khách theo từng kết nối (cụm). `sources` = chỉ lấy cụm của các nguồn đó. */
export function useContactAttributes(id: string | undefined, sources?: string[]) {
  return useQuery<{ clusters: import('@/lib/attr-format').ConnCluster[] }>({
    queryKey: ['contact', id, 'attributes', sources?.join(',') ?? 'all'],
    enabled: !!id,
    staleTime: 30_000,
    queryFn: async () => (await api.get(`/contacts/${id}/attributes`, { params: sources?.length ? { source: sources.join(',') } : undefined })).data,
  })
}
